import * as React from 'react'

import { Box, Button, Icon, Spinner, Text } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import TextField from 'modules/core/components/TextField'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { useActions } from 'modules/processes/hooks/actions/useActions'
import type Survey from 'modules/surveys/store/models/v2/Survey'

interface Props {
  survey: Survey
  onClose: (...args: unknown[]) => void
}

const LinkedWorkflowAction = ({
  survey,
  setLoading,
}: {
  survey: Survey
  setLoading: (value: boolean) => void
}) => {
  const params = JSON.stringify({ survey_id: survey.id })

  const { loading, actions } = useActions({
    configuration: params,
  })

  useEffect(() => {
    setLoading(loading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  if (actions.length === 0) return null

  return (
    <Box background="radicalRed100" borderRadius={{ all: 'abs008' }} padding="s24" gap="s8">
      <Box flexDirection="row" gap="s4">
        <Icon.Medium icon="alert-circle-line" color="radicalRed1000" />
        <Text.Normal color="grey1000" weight="semibold">
          {i18n.t('surveys.form.delete.linked_workflow.title')}
        </Text.Normal>
      </Box>
      <Text.Normal color="grey1000" weight="normal">
        {i18n.t('surveys.form.delete.linked_workflow.description')}
      </Text.Normal>
    </Box>
  )
}

export default function DeleteSurveyModal({ survey: form, onClose }: Props) {
  const [inputName, setInputName] = React.useState<string>('')
  const [loading, setLoading] = React.useState<boolean>(false)
  const name = form.attributes.name

  const description = i18n.t('surveys.form.delete.survey_description')

  const deleteButton = i18n.t('surveys.form.delete.survey_submit')

  const nameInputText = i18n.t('surveys.form.delete.survey_input_name', { name })

  const inputIsOk = inputName.trim() === name.trim()

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      if (!inputIsOk) return

      await form.destroy()
      closePortal()
    },
    {
      successMessage: i18n.t('surveys.form.delete.survey_deleted'),
    }
  )

  const disabled = running || !inputIsOk

  return (
    <Modal
      description={description}
      footer={
        <Button type="negative" disabled={disabled} submit>
          {deleteButton}
        </Button>
      }
      illustration="surveys"
      onClose={onClose}
      onSubmit={onSubmit}
      title={i18n.t('surveys.form.delete.title', {
        name,
      })}
    >
      <Box flexDirection="column" gap="s8">
        {loading ? (
          <Box justifyContent="center" alignItems="center">
            <Spinner />
          </Box>
        ) : (
          <>
            <Text.Normal whiteSpace="breakSpaces">{nameInputText}</Text.Normal>
            <TextField onChange={setInputName} value={inputName} />
          </>
        )}
      </Box>
      {form.isWorkflowSurvey && (
        <Box paddingTop="s16">
          <LinkedWorkflowAction survey={form} setLoading={setLoading} />
        </Box>
      )}
    </Modal>
  )
}
